import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import GooglePlacesAutoComplete from '@atom/components/common/GooglePlacesAutoComplete';
import { getGeoJSONLocation, parseCoordinates } from '@atom/map';
import { Icon, IconButton } from '@atom/mui';
import { CloseIcon, SwapVertIcon } from '@atom/mui/icons';
import { getLatLng } from '@atom/utilities/locationUtilities';

import CreateAssetModalContext from '../CreateAssetModalContext';

import CoordinatesInput from './CoordinatesInput';

import classes from './pointLocationTab.module.css';

enum InputMode {
  ADDRESS = 'ADDRESS',
  COORDINATES = 'COORDINATES',
}

const LocationInputs = () => {
  const anchor = useRef<HTMLDivElement>(null);

  const { location, setLocation, address, setAddress, setLocationFromSearch } =
    useContext(CreateAssetModalContext);

  const [inputMode, setInputMode] = useState<InputMode>(InputMode.ADDRESS);
  const [updateKey, setUpdateKey] = useState<number>(0);
  const [coordinatesInput, setCoordinatesInput] = useState<string>();
  const [invalidCoordinates, setInvalidCoordinates] = useState<boolean>(false);

  useEffect(() => {
    if (location) {
      const locationData = getLatLng(location?.coordinates);
      setCoordinatesInput(
        locationData ? `${locationData?.lat}, ${locationData?.lng}` : '',
      );
    }
  }, [location]);

  const onLocationChange = (
    newLocation: { lat: number; lng: number },
    searchTerm?: string,
  ) => {
    setAddress(searchTerm);
    setLocation(getGeoJSONLocation(newLocation));
    setLocationFromSearch(true);
    setInvalidCoordinates(false);
  };

  const handleClear = () => {
    setUpdateKey(prevKey => prevKey + 1);
    setLocation(null);
    setAddress(null);
    setCoordinatesInput(null);
    setInvalidCoordinates(false);
    setLocationFromSearch(false);
  };

  const handleCoordinatesChange = newCoordinatesInput => {
    setCoordinatesInput(newCoordinatesInput);

    const coordinates = parseCoordinates(newCoordinatesInput);

    if (coordinates) {
      setInvalidCoordinates(false);
      setLocation(getGeoJSONLocation(coordinates));
      setLocationFromSearch(true);
    } else {
      setInvalidCoordinates(true);
      setLocation(null);
      setAddress(null);
    }
  };

  const content = useMemo(() => {
    const error = (!address && !location) || invalidCoordinates;

    const inputContainerStyle = clsx(
      classes.inputContainer,
      error && classes.error,
    );
    const inputSubtextStyle = clsx(
      classes.inputSubtext,
      error && classes.error,
    );

    const addressModeSubtext = error
      ? 'Please provide a valid address.'
      : coordinatesInput;
    const coordinateModeSubtext = error
      ? 'Please provide valid coordinates (eg. 12.34567, -12,34567).'
      : address;

    const inputContent = {
      [InputMode.ADDRESS]: (
        <>
          <div ref={anchor} className={inputContainerStyle}>
            <GooglePlacesAutoComplete
              key={updateKey}
              hintText="Enter address"
              initialValue={address}
              onSelect={onLocationChange}
              anchor={anchor.current}
              popoverWidth="35rem"
              textFieldStyle={{ fontSize: 14 }}
            />
            <IconButton
              onClick={handleClear}
              size="small"
              data-cy="createAssetLocationClearButton"
            >
              <Icon icon={CloseIcon} />
            </IconButton>
          </div>
          <div className={inputSubtextStyle}>{addressModeSubtext}</div>
        </>
      ),
      [InputMode.COORDINATES]: (
        <>
          <div className={inputContainerStyle}>
            <CoordinatesInput
              coordinatesInput={coordinatesInput}
              handleCoordinatesChange={handleCoordinatesChange}
            />
            <IconButton
              onClick={handleClear}
              size="small"
              data-cy="createAssetCoordinatesClearButton"
            >
              <Icon icon={CloseIcon} />
            </IconButton>
          </div>
          <div className={inputSubtextStyle}>{coordinateModeSubtext}</div>
        </>
      ),
    };

    return inputContent[inputMode];
  }, [
    updateKey,
    anchor,
    address,
    onLocationChange,
    handleClear,
    coordinatesInput,
    setCoordinatesInput,
    handleCoordinatesChange,
    inputMode,
  ]);

  const toggleInputMode = () => {
    setInputMode(
      inputMode === InputMode.ADDRESS
        ? InputMode.COORDINATES
        : InputMode.ADDRESS,
    );
  };

  const swapTooltip =
    inputMode === InputMode.ADDRESS
      ? 'Switch to Coordinates Mode'
      : 'Switch to Address Mode';

  return (
    <div className={classes.inputsContainer}>
      <div className={classes.inputRow}>{content}</div>
      <IconButton
        onClick={toggleInputMode}
        tooltip={swapTooltip}
        data-cy="createAssetLocationToggleModeButton"
      >
        <Icon icon={SwapVertIcon} />
      </IconButton>
    </div>
  );
};

export default LocationInputs;
